/** 날짜는 한국 날짜 문자열(YYYY-MM-DD)로 다룬다. 기간은 시작일·종료일 양끝을 포함한다. */
export const APP_TIMEZONE = 'Asia/Seoul';
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/;

export function isIsoDate(value: string): boolean {
  const match = DATE_PATTERN.exec(value);
  if (!match) return false;
  const [, y, m, d] = match.map(Number) as [number, number, number, number];
  if (y < 1900 || y > 9998 || m < 1 || m > 12 || d < 1) return false;
  return d <= daysInMonth(y, m);
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function monthEnd(month: string): string {
  if (!MONTH_PATTERN.test(month)) throw new RangeError(`잘못된 월: ${month}`);
  const [y, m] = month.split('-').map(Number) as [number, number];
  return `${month}-${String(daysInMonth(y, m)).padStart(2, '0')}`;
}

export function koreaDate(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: APP_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** 양끝 포함 일수. */
export function daysInclusive(start: string, end: string): number {
  return Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000) + 1;
}

export interface DateRange {
  start: string;
  end: string;
}

export class RangeInputError extends Error {}

/**
 * 조회 입력을 날짜 범위로 바꾼다. 연도 2026 → 2026-01-01~12-31, 월 2026-02~2026-03 → 02-01~03-31.
 */
export function makeRange(mode: 'year' | 'months' | 'days', start: string, end: string = start): DateRange {
  let range: DateRange;
  if (mode === 'year') {
    if (!/^\d{4}$/.test(start) || +start < 1900 || +start > 9998) throw new RangeInputError('연도는 1900~9998 사이로 입력해주세요.');
    range = { start: `${start}-01-01`, end: `${start}-12-31` };
  } else if (mode === 'months') {
    if (!MONTH_PATTERN.test(start) || !MONTH_PATTERN.test(end)) throw new RangeInputError('월은 YYYY-MM 형식으로 입력해주세요.');
    range = { start: `${start}-01`, end: monthEnd(end) };
  } else {
    if (!isIsoDate(start) || !isIsoDate(end)) throw new RangeInputError('존재하는 날짜를 선택해주세요.');
    range = { start, end };
  }
  validateRange(range);
  return range;
}

export function validateRange(range: DateRange): void {
  if (!isIsoDate(range.start) || !isIsoDate(range.end)) throw new RangeInputError('존재하는 날짜를 선택해주세요.');
  if (range.start > range.end) throw new RangeInputError('시작일이 종료일보다 늦습니다.');
  if (+range.end.slice(0, 4) - +range.start.slice(0, 4) > 100) throw new RangeInputError('조회 기간은 100년 이내로 지정해주세요.');
}
